import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ConversationListItemProps {
  conversation: {
    id: string;
    customer_phone: string;
    customer_name: string | null;
    last_message_at: string;
    unread_count: number;
  };
  phoneNumberId: string;
}

export function ConversationListItem({ conversation, phoneNumberId }: ConversationListItemProps) {
  const displayName = conversation.customer_name || conversation.customer_phone;
  const formattedPhone = conversation.customer_phone.replace(
    /^\+?1?(\d{3})(\d{3})(\d{4})$/,
    '($1) $2-$3'
  );

  return (
    <Link to={`/admin/sms-phone-numbers/${phoneNumberId}/conversations/${conversation.id}`}>
      <Card className={`p-3 md:p-4 hover:shadow-md transition-all cursor-pointer border-l-4 min-h-[72px] active:bg-muted/50 ${
        conversation.unread_count > 0 ? 'border-l-primary bg-primary/5' : 'border-l-transparent'
      }`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-muted flex-shrink-0">
              <User className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 md:mb-1">
                <h3 className={`font-semibold text-sm md:text-base truncate ${
                  conversation.unread_count > 0 ? 'text-foreground' : 'text-foreground/80'
                }`}>
                  {displayName}
                </h3>
                {conversation.unread_count > 0 && (
                  <Badge variant="default" className="rounded-full px-2 py-0.5 text-xs bg-primary flex-shrink-0">
                    {conversation.unread_count}
                  </Badge>
                )}
              </div>
              
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                {conversation.customer_name ? formattedPhone : 'Tap to start conversation'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2 ml-2 md:ml-4 flex-shrink-0">
            <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">
              {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })}
            </span>
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground" />
          </div>
        </div>
      </Card>
    </Link>
  );
}
